import { useVirtualizer } from '@tanstack/react-virtual';
import { ReactNode, useRef } from 'react';

/**
 * The default number of items to render in the visible area.
 * This is set at a global level, but can be overridden per-instance.
 */
export const DEFAULT_VISIBLE_COUNT = 10;

/**
 * * **IMPLEMENTATION NOTE**: This Virtualizer implementation uses the **spacer** pattern, which is generally more compatible with all styling, but can be less performant with flat, massive lists. Should be reasonably good enough for grouped lists.
 *
 * Render a vertically virtualized list of `items` using [`@tanstack/react-virtual`](https://tanstack.com/virtual/latest/docs/framework/react/react-virtual).
 *
 * Take care to try and make the `estimateSize` for an item as accurate as possible, if the element cannot be measured dynamically, it can cause unexpected results.
 * If the size is too big, the elements will almost have a minimum size, and if it's too small, they may overlap with each other.
 *
 * Any excessive number of items should likely be paginated instead of virtualized; although, this will work for [Infinite Scroll](https://tanstack.com/virtual/latest/docs/framework/react/examples/infinite-scroll).
 *
 * Importantly, the if the size and quantity of the items exceeds the browser's precision for `height`, only part of the list will be accessible through scrolling.
 * This may be fixed with scaling in the future, or we may consider an alternative package.
 *
 * "CSS theoretically supports infinite precision and infinite ranges for all value types; however in reality implementations have finite capacity. UAs should support reasonably useful ranges and precisions." [(W3C CSS Values)](https://www.w3.org/TR/css3-values/#lengths:~:text=CSS%20theoretically%20supports%20infinite%20precision%20and%20infinite%20ranges%20for%20all%20value%20types%3B%20however%20in%20reality%20implementations%20have%20finite%20capacity.%20UAs%20should%20support%20reasonably%20useful%20ranges%20and%20precisions.)
 *
 * @see [Large Set of Data Not Fully Rendered #460](https://github.com/TanStack/virtual/issues/460)
 * @see [Reach Browser Max Height Pixels #616](https://github.com/TanStack/virtual/issues/616)
 * @see [What's the Maximum Pixel Value of CSS Width and Height Properties?](https://stackoverflow.com/questions/16637530/whats-the-maximum-pixel-value-of-css-width-and-height-properties/16637689#16637689)
 * @see [Should I Drop Scaling Support `react-virtualized` #396](https://github.com/bvaughn/react-virtualized/issues/396)
 *
 * Regarding unit testing the tests for this component provide a utility called `mockVirtualized` which can be used to mock the virtualized environment.
 */
export default function Virtualizer<T>({
  items,
  estimateSize,
  children: renderItem,
  visibleCount = DEFAULT_VISIBLE_COUNT,
}: {
  items: T[];
  /**
   * A function that returns the estimated size of an item at the given `index`.
   * Take care to make this as accurate as possible, if the element cannot be measured dynamically, it can cause unexpected results.
   * If the size is too big, the elements will almost have a minimum size, and if it's too small, they may overlap with each other.
   */
  estimateSize: (index: number) => number;
  children: (item: T, index: number) => ReactNode;
  /**
   * The maximum number of items visible in the scroll area. Determines the `max-height` of the wrapper with average item height.
   */
  visibleCount?: number;
}) {
  const parent = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parent.current,
    estimateSize,
  });

  if (!items.length) return null;

  // ? consider warning if the total size exceeds the current precision
  const totalSize = virtualizer.getTotalSize();
  const averageSize = Math.floor(totalSize / items.length);

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      ref={parent}
      className="max-h-(--virtual-wrapper-height) w-full overflow-auto"
      style={{
        '--virtual-wrapper-height': `min(${visibleCount * averageSize}px, 50svh)`,
      }}
      data-virtual-root
    >
      <div
        className="h-(--virtual-height) w-full"
        style={{ '--virtual-height': `${totalSize}px` }}
      >
        <div
          data-virtual-spacer="top"
          className="h-(--virtual-spacer-size)"
          style={{
            '--virtual-spacer-size': `${virtualItems.at(0)?.start ?? 0}px`,
          }}
        />
        {virtualItems.map((virtualItem) => {
          const item = items[virtualItem.index];
          if (item === undefined) return null; // just really to satisfy TypeScript
          return (
            <div
              key={virtualItem.key}
              data-virtual-item
              /**
               * `data-index` and the `measureElement` ref are required for dynamic sizing and accurate measurements.
               * @see https://tanstack.com/virtual/latest/docs/api/virtualizer#measureelement-1
               */
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
            >
              {renderItem(item, virtualItem.index)}
            </div>
          );
        })}
        <div
          data-virtual-spacer="bottom"
          className="h-(--virtual-spacer-size)"
          style={{
            '--virtual-spacer-size': `${totalSize - (virtualItems.at(-1)?.end ?? 0)}px`,
          }}
        />
      </div>
    </div>
  );
}
